import React, { useState, useEffect, useCallback } from "react";
import {
  Shield,
  Hammer,
  Search,
  Trash2,
  UserMinus,
  UserCheck,
  AlertTriangle,
  Package,
  Sparkles,
  CheckCircle,
  XCircle,
  Globe,
  Users,
  AlertCircle,
  List,
  UserCog,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { callBackend } from "../utils/api";
import { log } from "../utils/logger";

export default function AdminPanel({ role }) {
  const [activeTab, setActiveTab] = useState("intelligence");
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [targetPlayer, setTargetPlayer] = useState(null);

  // Directory State
  const [playerList, setPlayerList] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState("all");

  // Global State
  const [appeals, setAppeals] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [itemGrant, setItemGrant] = useState({ itemId: "", quantity: 1 });
  const [devStats, setDevStats] = useState({
    cash: 1000000,
    level: 50,
    experience: 250000,
    turns: 500,
  });

  // Fetch Directory Data
  const fetchDirectory = useCallback(async () => {
    try {
      const data = await callBackend(
        `/admin/players?page=${page}&filter=${filter}&search=${searchQuery}`,
      );
      setPlayerList(data.players || []);
      setTotalPages(data.pages || 1);
    } catch (err) {
      log("ADMIN", "Failed to fetch directory", err);
    }
  }, [page, filter, searchQuery]);

  useEffect(() => {
    if (activeTab === "directory") fetchDirectory();
    if (activeTab === "appeals") callBackend("/admin/appeals").then(setAppeals);
    if (role === "dev") callBackend("/shop/inventory").then(setAllItems);
  }, [activeTab, fetchDirectory, role]);

  // Search Logic with Suggestions
  const handleSearchChange = async (val) => {
    setSearchQuery(val);
    if (val.length > 1) {
      try {
        const list = await callBackend(`/admin/search-suggestions?q=${val}`);
        setSuggestions(list || []);
      } catch (err) {
        setSuggestions([]);
      }
    } else {
      setSuggestions([]);
    }
  };

  const handleSelectPlayer = async (username) => {
    if (!username) return;
    setSuggestions([]);
    try {
      const data = await callBackend(`/admin/player/${username}`);
      setTargetPlayer(data);
      setSearchQuery(data.username);
      setActiveTab("intelligence");
    } catch (err) {
      alert("User not found.");
    }
  };

  // Moderation Actions
  const handleBanAction = async (type) => {
    const reason = window.prompt("Reason for ban?");
    if (!reason) return;
    await callBackend("/admin/player/ban", {
      method: "POST",
      body: {
        targetId: targetPlayer.id,
        type,
        reason,
        duration: type === "temp" ? 24 : 0,
      },
    });
    handleSelectPlayer(targetPlayer.username);
  };

  const handleUnban = async () => {
    await callBackend("/admin/player/unban", {
      method: "POST",
      body: { targetId: targetPlayer.id },
    });
    handleSelectPlayer(targetPlayer.username);
  };

  const handleWipe = async () => {
    if (!window.confirm("TOTALLY WIPE INVENTORY?")) return;
    await callBackend("/admin/player/wipe", {
      method: "POST",
      body: { targetId: targetPlayer.id },
    });
    handleSelectPlayer(targetPlayer.username);
  };

  const handleResolve = async (appealId, status) => {
    await callBackend("/admin/appeals/resolve", {
      method: "POST",
      body: { appealId, status },
    });
    setAppeals(appeals.filter((a) => a.id !== appealId));
  };

  // Developer Actions
  const handleGiveStats = async () => {
    await callBackend("/admin/stats", {
      method: "POST",
      body: { ...devStats, targetId: targetPlayer?.id },
    });
    alert(`Stats applied to ${targetPlayer?.username || "self"}.`);
    if (targetPlayer) handleSelectPlayer(targetPlayer.username);
  };

  const handleAddItem = async () => {
    if (!itemGrant.itemId) return alert("Select an item first.");
    await callBackend("/admin/inventory/add", {
      method: "POST",
      body: { ...itemGrant, targetId: targetPlayer?.id },
    });
    alert("Item granted.");
    if (targetPlayer) handleSelectPlayer(targetPlayer.username);
  };

  return (
    <div className="flex flex-col h-full font-mono text-white bg-black">
      {/* Navigation Bar */}
      <div className="flex border-b-2 border-zinc-800 bg-zinc-950">
        {[
          { id: "intelligence", icon: Search, label: "Intelligence" },
          { id: "directory", icon: List, label: "Directory" },
          { id: "appeals", icon: Shield, label: "Appeals" },
          {
            id: "management",
            icon: UserCog,
            label: "Global Ops",
            hidden: role !== "dev",
          },
        ]
          .filter((t) => !t.hidden)
          .map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 text-[10px] font-black uppercase italic transition-all ${
                activeTab === tab.id
                  ? "bg-zinc-800 text-yellow-500 border-b-2 border-yellow-500"
                  : "text-zinc-500 hover:text-white"
              }`}
            >
              <tab.icon size={14} /> {tab.label}
            </button>
          ))}
      </div>

      <div className="p-6 overflow-y-auto flex-1">
        {/* Persistent Search Bar */}
        <div className="relative mb-8 max-w-2xl">
          <div className="flex gap-2">
            <input
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && handleSelectPlayer(searchQuery)
              }
              className="flex-1 bg-zinc-900 border border-zinc-700 p-3 text-white uppercase font-bold outline-none focus:border-yellow-500"
              placeholder="SCAN USERNAME..."
            />
            <button
              onClick={() => handleSelectPlayer(searchQuery)}
              className="bg-white text-black px-8 font-black uppercase italic hover:bg-yellow-500 transition-colors"
            >
              Scan
            </button>
          </div>
          {suggestions.length > 0 && (
            <div className="absolute z-50 w-full bg-zinc-900 border border-zinc-700 mt-1 shadow-2xl">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => handleSelectPlayer(s)}
                  className="w-full text-left p-3 hover:bg-zinc-800 border-b border-zinc-800 last:border-0 text-xs uppercase font-bold"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === "intelligence" && (
          <>
            {targetPlayer ? (
              <div className="animate-in fade-in slide-in-from-bottom-4 space-y-6">
                {/* Header Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-2 bg-zinc-900 p-6 border-l-4 border-yellow-500 flex justify-between items-center">
                    <div>
                      <p className="text-3xl font-black italic uppercase text-white">
                        {targetPlayer.username}
                      </p>
                      <p className="text-[10px] text-zinc-500 font-black uppercase">
                        Role: {targetPlayer.role} | Level: {targetPlayer.level}{" "}
                        | Cash: ${targetPlayer.cash.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p
                        className={`text-sm font-black uppercase italic ${
                          targetPlayer.is_banned
                            ? "text-red-500"
                            : "text-green-500"
                        }`}
                      >
                        {targetPlayer.is_banned ? "Restricted" : "Active"}
                      </p>
                      {targetPlayer.is_banned && (
                        <p className="text-[9px] text-zinc-600 uppercase">
                          {targetPlayer.ban_reason}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleBanAction("temp")}
                      className="flex items-center justify-center gap-2 bg-yellow-900/10 border border-yellow-700/50 text-yellow-500 text-[9px] font-black uppercase italic hover:bg-yellow-700 hover:text-black"
                    >
                      <UserMinus size={12} /> Detain
                    </button>
                    <button
                      onClick={() => handleBanAction("perm")}
                      className="flex items-center justify-center gap-2 bg-red-900/10 border border-red-700/50 text-red-500 text-[9px] font-black uppercase italic hover:bg-red-700 hover:text-black"
                    >
                      <AlertTriangle size={12} /> Arrest
                    </button>
                    <button
                      onClick={handleUnban}
                      className="flex items-center justify-center gap-2 bg-green-900/10 border border-green-700/50 text-green-500 text-[9px] font-black uppercase italic hover:bg-green-700 hover:text-black"
                    >
                      <UserCheck size={12} /> Release
                    </button>
                    {role === "dev" && (
                      <button
                        onClick={handleWipe}
                        className="flex items-center justify-center gap-2 bg-zinc-800 border border-zinc-600 text-white text-[9px] font-black uppercase italic hover:bg-white hover:text-black"
                      >
                        <Trash2 size={12} /> Wipe
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Connection Intelligence */}
                  <div className="bg-zinc-950 p-6 border border-zinc-900">
                    <h4 className="text-[10px] font-black uppercase text-zinc-600 mb-4 flex items-center gap-2">
                      <Globe size={14} /> Connection Intelligence
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-black p-3 border border-zinc-900">
                        <p className="text-[8px] text-zinc-700 uppercase font-black">
                          Last Known IP
                        </p>
                        <p className="text-blue-400 font-bold text-xs">
                          {targetPlayer.last_ip || "UNKNOWN"}
                        </p>
                      </div>
                      <div className="bg-black p-3 border border-zinc-900">
                        <p className="text-[8px] text-zinc-700 uppercase font-black">
                          Registration IP
                        </p>
                        <p className="text-zinc-500 font-bold text-xs italic">
                          {targetPlayer.registration_ip || "UNKNOWN"}
                        </p>
                      </div>
                      <div className="col-span-2 bg-black p-3 border border-zinc-900">
                        <p className="text-[8px] text-zinc-700 uppercase font-black mb-1">
                          Device Fingerprint
                        </p>
                        <p className="text-[9px] text-zinc-400 break-all leading-tight">
                          {targetPlayer.user_agent ||
                            "No device data captured."}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Linked Accounts */}
                  <div className="bg-zinc-950 p-6 border border-zinc-900">
                    <h4 className="text-[10px] font-black uppercase text-zinc-600 mb-4 flex items-center gap-2">
                      <Users size={14} /> Linked Accounts (Same IP)
                    </h4>
                    <div className="space-y-1 bg-black p-3 border border-zinc-900 max-h-40 overflow-y-auto">
                      {targetPlayer.linkedAccounts?.length > 0 ? (
                        targetPlayer.linkedAccounts.map((alt, idx) => (
                          <div
                            key={idx}
                            className="flex justify-between items-center py-1 border-b border-zinc-900 last:border-0"
                          >
                            <span
                              className="text-zinc-300 font-bold text-[10px] cursor-pointer hover:text-yellow-500"
                              onClick={() => handleSelectPlayer(alt.username)}
                            >
                              {alt.username}
                            </span>
                            <span
                              className={`text-[8px] font-black uppercase ${
                                alt.is_banned
                                  ? "text-red-500"
                                  : "text-green-500"
                              }`}
                            >
                              {alt.is_banned ? "Banned" : "Active"}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-[10px] text-zinc-800 italic text-center py-4">
                          No other accounts detected.
                        </p>
                      )}
                    </div>
                    {targetPlayer.linkedAccounts?.some((a) => a.is_banned) && (
                      <div className="mt-3 p-2 bg-red-900/10 border border-red-900/30 text-[9px] text-red-500 font-black uppercase flex items-center gap-2">
                        <AlertCircle size={12} /> Alt-Evasion Warning: Banned
                        Links Found
                      </div>
                    )}
                  </div>

                  {/* Inventory Log */}
                  <div className="lg:col-span-2 bg-zinc-950 p-6 border border-zinc-900">
                    <h4 className="text-[10px] font-black uppercase text-zinc-600 mb-4 flex items-center gap-2">
                      <Package size={14} /> Inventory Manifest
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                      {targetPlayer.inventory.map((i, idx) => (
                        <div
                          key={idx}
                          className="bg-black p-2 border border-zinc-900 text-center"
                        >
                          <p className="text-[9px] text-white font-black uppercase truncate">
                            {i.name}
                          </p>
                          <p className="text-[10px] text-zinc-600">
                            x{i.quantity}
                          </p>
                          <div className="w-full bg-zinc-900 h-0.5 mt-1">
                            <div
                              className="bg-green-500 h-full"
                              style={{ width: `${i.durability}%` }}
                            />
                          </div>
                        </div>
                      ))}
                      {targetPlayer.inventory.length === 0 && (
                        <p className="col-span-full text-[10px] text-zinc-800 italic py-8 text-center">
                          Empty Stash
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-96 border-2 border-dashed border-zinc-800 text-zinc-700 uppercase font-black italic">
                <Search size={48} className="mb-4 opacity-10" />
                Target Locked: Pending Scan
              </div>
            )}
          </>
        )}

        {activeTab === "directory" && (
          <div className="space-y-4 animate-in fade-in">
            <div className="flex justify-between items-center bg-zinc-900 p-4 border border-zinc-800">
              <div className="flex gap-2">
                {["all", "banned", "staff"].map((f) => (
                  <button
                    key={f}
                    onClick={() => {
                      setFilter(f);
                      setPage(1);
                    }}
                    className={`text-[9px] font-black uppercase px-4 py-1.5 border transition-all ${
                      filter === f
                        ? "bg-yellow-600 border-yellow-500 text-black shadow-[0_0_10px_rgba(202,138,4,0.3)]"
                        : "border-zinc-700 text-zinc-500 hover:border-zinc-500"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 bg-zinc-800 border border-zinc-700 text-white disabled:opacity-20"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="text-[10px] font-black uppercase">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 bg-zinc-800 border border-zinc-700 text-white disabled:opacity-20"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

            <div className="bg-zinc-950 border border-zinc-900">
              <table className="w-full text-left border-collapse">
                <thead className="bg-black text-[9px] uppercase text-zinc-600 border-b border-zinc-800">
                  <tr>
                    <th className="p-4">UID</th>
                    <th>Identity</th>
                    <th>Level</th>
                    <th>Net Worth</th>
                    <th>Status</th>
                    <th>Role</th>
                    <th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-[11px]">
                  {playerList.map((p) => (
                    <tr
                      key={p.id}
                      className="border-b border-zinc-900 hover:bg-zinc-900/40 transition-colors"
                    >
                      <td className="p-4 text-zinc-700 font-bold">#{p.id}</td>
                      <td className="font-black text-white italic">
                        {p.username}
                      </td>
                      <td>{p.level}</td>
                      <td className="text-green-500 font-bold">
                        ${p.cash.toLocaleString()}
                      </td>
                      <td>
                        <span
                          className={`px-2 py-0.5 text-[8px] font-black uppercase border ${
                            p.is_banned
                              ? "bg-red-900/10 text-red-500 border-red-900"
                              : "bg-green-900/10 text-green-500 border-green-900"
                          }`}
                        >
                          {p.is_banned ? "Restricted" : "Active"}
                        </span>
                      </td>
                      <td className="uppercase text-zinc-500 font-bold">
                        {p.role}
                      </td>
                      <td className="text-right p-4">
                        <button
                          onClick={() => handleSelectPlayer(p.username)}
                          className="text-yellow-500 hover:text-white font-black italic uppercase text-[9px]"
                        >
                          [ Inspect ]
                        </button>
                      </td>
                    </tr>
                  ))}
                  {playerList.length === 0 && (
                    <tr>
                      <td
                        colSpan="7"
                        className="p-12 text-center text-zinc-800 italic uppercase font-black"
                      >
                        No records found in this sector.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "appeals" && (
          <section className="bg-zinc-900 border-2 border-blue-600 p-6 animate-in fade-in">
            <h3 className="text-xl font-black text-blue-500 mb-6 uppercase flex items-center gap-2">
              <Shield /> Mod Queue: Pending Appeals
            </h3>
            <div className="space-y-4">
              {appeals.map((a) => (
                <div
                  key={a.id}
                  className="bg-black p-5 border border-zinc-800 flex justify-between items-center group hover:border-blue-900/50 transition-colors"
                >
                  <div
                    className="cursor-pointer flex-1"
                    onClick={() => handleSelectPlayer(a.username)}
                  >
                    <p className="text-blue-400 font-bold uppercase flex items-center gap-2">
                      {a.username} <Search size={10} className="opacity-30" />
                    </p>
                    <p className="text-xs text-zinc-400 italic mt-1 leading-relaxed">
                      "{a.message}"
                    </p>
                  </div>
                  <div className="flex gap-6 pl-6 border-l border-zinc-900">
                    <button
                      onClick={() => handleResolve(a.id, "approved")}
                      className="text-green-600 hover:text-green-400 transition-colors flex flex-col items-center gap-1"
                    >
                      <CheckCircle size={28} />
                      <span className="text-[7px] font-black uppercase">
                        Approve
                      </span>
                    </button>
                    <button
                      onClick={() => handleResolve(a.id, "denied")}
                      className="text-red-600 hover:text-red-400 transition-colors flex flex-col items-center gap-1"
                    >
                      <XCircle size={28} />
                      <span className="text-[7px] font-black uppercase">
                        Deny
                      </span>
                    </button>
                  </div>
                </div>
              ))}
              {appeals.length === 0 && (
                <div className="text-center py-20">
                  <p className="text-zinc-800 italic font-black uppercase">
                    The queue is clear. All restrictions upheld.
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {activeTab === "management" && role === "dev" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in fade-in">
            {/* STAT FORGE */}
            <section className="p-6 bg-zinc-900 border-2 border-yellow-600 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-2 opacity-5">
                <Hammer size={120} />
              </div>
              <h3 className="text-xl font-black text-yellow-500 mb-6 flex items-center gap-2 uppercase italic">
                <Hammer size={20} /> Stat Forge
              </h3>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { label: "Cash", key: "cash" },
                  { label: "Turns", key: "turns" },
                  { label: "Level", key: "level" },
                  { label: "Experience", key: "experience" },
                ].map((stat) => (
                  <div key={stat.key} className="space-y-1">
                    <label className="text-[9px] text-zinc-500 uppercase font-black">
                      {stat.label}
                    </label>
                    <input
                      type="number"
                      value={devStats[stat.key]}
                      onChange={(e) =>
                        setDevStats({ ...devStats, [stat.key]: e.target.value })
                      }
                      className="w-full bg-black border border-zinc-800 p-3 text-white font-bold focus:border-yellow-600 outline-none"
                    />
                  </div>
                ))}
              </div>
              <button
                onClick={handleGiveStats}
                className="w-full bg-yellow-600 px-4 py-4 text-black font-black uppercase italic hover:bg-yellow-400 transition-all shadow-[0_4px_0_rgb(113,63,18)] active:translate-y-1 active:shadow-none"
              >
                Apply Profile Overwrite{" "}
                {targetPlayer ? `to ${targetPlayer.username}` : "(Self)"}
              </button>
            </section>

            {/* ITEM INJECTION */}
            <section className="p-6 bg-zinc-900 border-2 border-purple-600 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-2 opacity-5">
                <Package size={120} />
              </div>
              <h3 className="text-xl font-black text-purple-500 mb-6 flex items-center gap-2 uppercase italic">
                <Package size={20} /> Stash Infusion
              </h3>
              <div className="space-y-4 mb-6">
                <div className="space-y-1">
                  <label className="text-[9px] text-zinc-500 uppercase font-black">
                    Hardware Selection
                  </label>
                  <select
                    className="w-full bg-black border border-zinc-800 p-4 text-purple-400 font-bold outline-none focus:border-purple-600 appearance-none"
                    value={itemGrant.itemId}
                    onChange={(e) =>
                      setItemGrant({ ...itemGrant, itemId: e.target.value })
                    }
                  >
                    <option value="">-- SELECT ASSET TO GRANT --</option>
                    {allItems.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name.toUpperCase()} (Lvl {i.min_level})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 space-y-1">
                    <label className="text-[9px] text-zinc-500 uppercase font-black">
                      Quantity
                    </label>
                    <input
                      type="number"
                      value={itemGrant.quantity}
                      onChange={(e) =>
                        setItemGrant({ ...itemGrant, quantity: e.target.value })
                      }
                      className="w-full bg-black border border-zinc-800 p-4 text-white font-bold focus:border-purple-600 outline-none"
                      placeholder="Amount"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={handleAddItem}
                      className="bg-purple-600 text-black px-10 py-4 font-black uppercase italic hover:bg-purple-400 transition-all flex items-center gap-2 shadow-[0_4px_0_rgb(88,28,135)] active:translate-y-1 active:shadow-none"
                    >
                      <Sparkles size={16} /> Grant
                    </button>
                  </div>
                </div>
              </div>
              <p className="text-[9px] text-zinc-500 uppercase italic font-medium bg-black/50 p-2 border border-zinc-800">
                Note: Injected assets are delivered directly to the target
                stash. No notification will be sent.
              </p>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
